/**
 * Which sent campaigns Renee has hidden from the Bulk tab's default view —
 * queue item 4's first half, "Hide from this list" plus a "show hidden"
 * toggle (pattern from `Tasks.tsx`'s done-tasks toggle).
 *
 * Client-only, the same first cut `views.ts` made for saved views: no
 * schema change for this half, nothing that can go wrong for however many
 * campaigns pile up, and the cost is honest and small — this is a list of
 * what she has already dismissed, not a record anyone else needs to read.
 * Moving it to a real column later (a `hidden_at` on `campaigns`, say)
 * changes the functions in this file and nothing else.
 *
 * Entirely independent of the 30-day auto-delete Worker
 * (`worker/cleanup-campaigns.js`) — hiding a campaign here does not save it
 * from that cleanup, and a campaign never hidden here is deleted on exactly
 * the same schedule as one that was. The two halves of this queue item don't
 * know about each other on purpose: hiding is about what's on screen today,
 * deleting is about what's worth keeping a record of at all, and conflating
 * them would mean "hide" quietly became "keep forever" or "delete" quietly
 * un-hid something she'd already dismissed.
 */
const KEY = 'agent-care.hidden-campaigns'

/** Malformed or missing storage reads as "nothing hidden" — see `loadSavedViews` in `views.ts` for the same posture. */
export function loadHiddenCampaignIds(): Set<string> {
  try {
    const raw = window.localStorage.getItem(KEY)
    if (!raw) return new Set()
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return new Set()
    return new Set(parsed.filter((v): v is string => typeof v === 'string'))
  } catch {
    return new Set()
  }
}

/** Returns whether the write actually landed — see `write` in `views.ts` for why that matters and cannot be swallowed. */
function persist(ids: Set<string>): boolean {
  try {
    window.localStorage.setItem(KEY, JSON.stringify([...ids]))
    return true
  } catch {
    return false
  }
}

export function hideCampaign(id: string): { ids: Set<string>; stored: boolean } {
  const ids = loadHiddenCampaignIds()
  ids.add(id)
  return { ids, stored: persist(ids) }
}

/** The way back — "Put it back" while "show hidden" is on. */
export function unhideCampaign(id: string): { ids: Set<string>; stored: boolean } {
  const ids = loadHiddenCampaignIds()
  ids.delete(id)
  return { ids, stored: persist(ids) }
}
